import {
  keepPreviousData,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  controlWorkflow,
  getIngestRunLineage,
  getIngestRunPages,
  resumeIngestRun,
  type WorkflowControlAction,
} from '../services/ingest-run.service';
import type {
  IngestResumeConflictCode,
  IngestResumeMode,
  IngestRunPagesParams,
} from '../types/ingest-run.types';
import { isLiveRunStatus, isRealPageRow, withDisplayLabels } from '../utils/ingest-run';

/** Live run data refreshes on the same cadence as the workflow status poll. */
export const INGEST_RUN_POLL_MS = 5_000;

/** After a resume 503 the workflow may still be starting: re-read the chain this
 *  long after, so a run that did start shows up without a manual refresh. */
export const RESUME_UNAVAILABLE_REFETCH_MS = 3_000;

const RESUME_CONFLICT_CODES: readonly IngestResumeConflictCode[] = [
  'RUN_NOT_RESUMABLE',
  'NEWER_RUN_EXISTS',
  'CONNECTION_BUSY',
  'NO_RESUME_POINT',
];

/** The resume endpoint's 409 `code`, read from the interceptor's `serverCode`
 *  or — if the backend nests it under FastAPI's `detail` — from the raw body. */
export function resumeConflictCode(err: unknown): IngestResumeConflictCode | null {
  if (!(err instanceof ApiError) || err.statusCode !== 409) return null;
  const body = err.body as { detail?: { code?: unknown } } | null | undefined;
  const nested = body?.detail && typeof body.detail === 'object' ? body.detail.code : undefined;
  const code = err.serverCode ?? (typeof nested === 'string' ? nested : undefined);
  return RESUME_CONFLICT_CODES.find((c) => c === code) ?? null;
}

/** The resume 409's human `detail` (e.g. RUN_NOT_RESUMABLE naming the product
 *  cap): the contract's top-level `detail` string, FastAPI's nested
 *  `detail.message`, else the interceptor's message. `null` when there is none. */
export function resumeConflictDetail(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { detail?: unknown } | null | undefined;
  const detail = body?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (detail && typeof detail === 'object') {
    const nested = (detail as { message?: unknown; detail?: unknown }).message;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return err.message.trim() || null;
}

/** True for the resume endpoint's 503 (Temporal did not start the workflow — or
 *  it may still be starting). The chain is unchanged as far as the server knows. */
export function isResumeUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.statusCode === 503;
}

/** True when a run read 404s: the run is not a product-ingestion run (export /
 *  audit / delivery), so it has no lineage or page ledger. */
export function isNotIngestionRun(err: unknown): boolean {
  return err instanceof ApiError && err.statusCode === 404;
}

/** Run chain for `runId`; polls while any run in it is still live, then stops. */
export function useIngestRunLineage(runId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.ingestRuns.lineage(runId ?? ''),
    queryFn: () => getIngestRunLineage(runId ?? ''),
    enabled: !!runId,
    select: withDisplayLabels,
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => isLiveRunStatus(r.status)) ? INGEST_RUN_POLL_MS : false,
  });
}

/** One server-side page of a run's page ledger. Polls only while that run is
 *  live; keeps the previous page on screen while the next one loads. */
export function useIngestRunPages(
  runId: string | null | undefined,
  params: IngestRunPagesParams,
  live: boolean,
) {
  return useQuery({
    queryKey: queryKeys.ingestRuns.pages(runId ?? '', params),
    queryFn: () => getIngestRunPages(runId ?? '', params),
    enabled: !!runId,
    placeholderData: keepPreviousData,
    refetchInterval: live ? INGEST_RUN_POLL_MS : false,
  });
}

/** The live run's current page (first `ACTIVE` row, pages ascend) — drives the
 *  "retrying" / "taking longer than usual" banners. Off once the run is terminal.
 *  A virtual `PENDING` row is not a current page: without a ledger (worker flag
 *  off) the first ACTIVE row is always the run's `fromPage`, whatever it is on. */
export function useIngestCurrentPage(runId: string | null | undefined, live: boolean) {
  const params: IngestRunPagesParams = { status: 'ACTIVE', offset: 0, limit: 1 };
  return useQuery({
    queryKey: queryKeys.ingestRuns.pages(runId ?? '', params),
    queryFn: () => getIngestRunPages(runId ?? '', params),
    enabled: !!runId && live,
    refetchInterval: live ? INGEST_RUN_POLL_MS : false,
    select: (data) => data.items.find(isRealPageRow) ?? null,
  });
}

/** The page a stopped run died on (its one `STOPPED` row) — its last attempt's
 *  `code` goes into the support reference. */
export function useIngestStoppedPage(runId: string | null | undefined, enabled: boolean) {
  const params: IngestRunPagesParams = { status: 'STOPPED', offset: 0, limit: 1 };
  return useQuery({
    queryKey: queryKeys.ingestRuns.pages(runId ?? '', params),
    queryFn: () => getIngestRunPages(runId ?? '', params),
    enabled: !!runId && enabled,
    select: (data) => data.items[0] ?? null,
  });
}

/** Pause / resume / cancel the running import workflow. Refreshes the status
 *  poll and the run reads on success; errors surface as a toast. */
export function useWorkflowControl(workflowId: string) {
  const { t } = useTranslation('onboarding');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: WorkflowControlAction) => controlWorkflow(workflowId, action),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.workflows.status(workflowId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.ingestRuns.all }),
      ]);
    },
    onError: (err, action) => {
      toast.error(extractApiError(err, t(`completeImport.run.toasts.${action}Error`)));
    },
  });
}

/** 409s after which the screen's view of the chain is stale: re-read it (and the
 *  workflow status) so the card can follow the newer run. */
const STALE_CHAIN_CODES: readonly IngestResumeConflictCode[] = [
  'NEWER_RUN_EXISTS',
  'RUN_NOT_RESUMABLE',
];

/** True for the 409 codes after which the caller should follow the chain's latest run. */
export function isStaleChainConflict(code: IngestResumeConflictCode | null): boolean {
  return code !== null && STALE_CHAIN_CODES.includes(code);
}

/** Start a new run from a FAILED/CANCELLED one. A 202 toasts the page the server
 *  actually resumed from. What the user sees on a failure is split with the caller:
 *  - `NO_RESUME_POINT` → nothing here; the dialog switches to RESTART inline.
 *  - stale-chain 409 (`NEWER_RUN_EXISTS` / `RUN_NOT_RESUMABLE`) → re-reads the
 *    lineage and the workflow status (awaited, so the caller's own `onError` sees
 *    fresh data) and leaves the message to the caller, which knows whether it
 *    actually found a newer run to follow.
 *  - 503 → toasts the server's text and re-reads the chain shortly after: the run
 *    may still be starting, so the screen stays on the current run meanwhile.
 *  - `CONNECTION_BUSY` → a "try again" toast with the server's detail (another
 *    import is running, or this run is still stopping right after a cancel).
 *  - anything else → a toast. */
export function useResumeIngestRun(workflowId?: string) {
  const { t } = useTranslation('onboarding');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, mode }: { runId: string; mode: IngestResumeMode }) =>
      resumeIngestRun(runId, mode),
    onSuccess: async (result) => {
      toast.success(
        result.kind === 'RESTART'
          ? t('completeImport.run.toasts.restarted')
          : t('completeImport.run.toasts.resumedFromPage', { page: result.fromPage }),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.ingestRuns.all });
    },
    onError: async (err, { runId }) => {
      const rereadChain = () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.ingestRuns.lineage(runId) }),
          workflowId
            ? queryClient.invalidateQueries({ queryKey: queryKeys.workflows.status(workflowId) })
            : undefined,
        ]);
      const code = resumeConflictCode(err);
      if (code === 'NO_RESUME_POINT') return;
      if (isStaleChainConflict(code)) {
        await rereadChain();
        return;
      }
      if (code === 'CONNECTION_BUSY') {
        // Two causes share the code: another import is running, or this run is still
        // stopping (a resume clicked right after a cancel). Both are worth retrying.
        toast.error(t('completeImport.run.conflicts.CONNECTION_BUSY'), {
          description: resumeConflictDetail(err) ?? undefined,
        });
        return;
      }
      if (code) {
        toast.error(t(`completeImport.run.conflicts.${code}`));
        return;
      }
      if (isResumeUnavailable(err)) {
        toast.error(extractApiError(err, t('completeImport.run.toasts.resumeUnavailable')));
        setTimeout(() => {
          void rereadChain();
        }, RESUME_UNAVAILABLE_REFETCH_MS);
        return;
      }
      toast.error(extractApiError(err, t('completeImport.run.toasts.continueError')));
    },
  });
}

/**
 * Refetch `keys` once when `live` flips true → false. Polling stops the moment a
 * run turns terminal, so without this the last poll's data (pages "in progress",
 * events missing the final transition) would sit in the cache as fresh.
 */
export function useRefetchWhenSettled(live: boolean, keys: readonly QueryKey[]) {
  const queryClient = useQueryClient();
  const wasLive = useRef(live);
  // Serialised so a fresh array literal each render doesn't re-run the effect.
  const keysJson = JSON.stringify(keys);
  useEffect(() => {
    const settled = wasLive.current && !live;
    wasLive.current = live;
    if (!settled) return;
    for (const queryKey of JSON.parse(keysJson) as QueryKey[]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [live, keysJson, queryClient]);
}
